import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('FORM_SUBMIT_URL', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-32 bg-gradient-to-br from-esthe-floral-white via-white to-esthe-pale-taupe/20">
      <div className="max-w-7xl mx-auto px-8">
        <div className="text-center mb-20">
          <p className="text-esthe-light-bronze text-base tracking-widest uppercase mb-6 font-medium">
            / GET IN TOUCH
          </p>
          <h2 className="text-6xl md:text-7xl font-serif mb-6 leading-tight text-esthe-cafe-noir">
            Schedule Your
            <br />
            Consultation Today
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-20 items-start">
          {/* Contact Information */}
          <div className="space-y-12">
            <div className="flex items-start gap-8">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-esthe-light-bronze to-esthe-mocha rounded-2xl flex-shrink-0">
                <i className="ri-phone-line text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-serif text-esthe-cafe-noir mb-3">Phone</h3>
                <a href="tel:+15551234567" className="text-xl text-esthe-mocha hover:text-esthe-light-bronze transition-colors cursor-pointer">
                  +1 (555) 123-4567
                </a>
              </div>
            </div>

            <div className="flex items-start gap-8">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-esthe-light-bronze to-esthe-mocha rounded-2xl flex-shrink-0">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-serif text-esthe-cafe-noir mb-3">Email</h3>
                <a href="mailto:info@esthe-clinics.com" className="text-xl text-esthe-mocha hover:text-esthe-light-bronze transition-colors cursor-pointer">
                  info@esthe-clinics.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-8">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-esthe-light-bronze to-esthe-mocha rounded-2xl flex-shrink-0">
                <i className="ri-map-pin-line text-3xl text-white"></i>
              </div>
              <div>
                <h3 className="text-2xl font-serif text-esthe-cafe-noir mb-3">Location</h3>
                <p className="text-xl text-esthe-mocha leading-relaxed">
                  Eastlane by urbnlanes<br />
                  New Cairo 1, Cairo Governorate 4740003
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  required
                  className="w-full px-0 py-4 text-base bg-transparent border-b-2 border-esthe-pale-taupe focus:border-esthe-light-bronze outline-none transition-colors"
                />
              </div>

              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  required
                  className="w-full px-0 py-4 text-base bg-transparent border-b-2 border-esthe-pale-taupe focus:border-esthe-light-bronze outline-none transition-colors"
                />
              </div>

              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  required
                  className="w-full px-0 py-4 text-base bg-transparent border-b-2 border-esthe-pale-taupe focus:border-esthe-light-bronze outline-none transition-colors"
                />
              </div>

              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your aesthetic goals..."
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-0 py-4 text-base bg-transparent border-b-2 border-esthe-pale-taupe focus:border-esthe-light-bronze outline-none transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-esthe-mocha mt-1">Maximum 500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-esthe-blush-pink to-esthe-deep-carmine text-white rounded-full text-base font-medium hover:from-esthe-deep-carmine hover:to-esthe-blush-pink transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <p className="text-esthe-light-bronze text-center">Thank you! We'll be in touch soon.</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-center">Something went wrong. Please try again.</p>
              )}

              <p className="text-xs text-esthe-mocha text-center">
                By submitting this form, you agree to our{' '}
                <a href="#" className="text-esthe-light-bronze hover:underline cursor-pointer">
                  Privacy Policy
                </a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
